import React, { useCallback, useEffect, useState } from "react";
import Modal from "components/global/modal";
import Button from "components/button/button";
import ModalHeader from "components/global/modal-header";
import LoadingIndicator from "assets/svg/loading-indicator";
import _ from "lodash";
import CustomCheckbox from "components/checkbox/custom-checkbox";
import { KB, MB, maxUploadSize } from "utils/constant";
import { useDialogHook } from "utils/customhooks";
import UploadSizeModal from "components/file-upload/upload-size-modal";
import { RxDragHandleDots2 } from "react-icons/rx";
import EditPencilIconSVG from "assets/svg/edit-pencil-icon";
import TrashIconSVG from "assets/svg/trash-icon";
import DropzoneUploadGallery from "./dropzone-gallery";

import { Container, Draggable } from "@edorivai/react-smooth-dnd";
import CheckIconSVG from "assets/svg/check-icon";
import { useTranslation } from "react-i18next";

const UPLOAD = "Upload";
const RENAME = "Rename";

export default function AddPhotoDraggableModal({
  hide,
  userData,
  profile,
  onUploadImagesVideos = async () => {},
  ...props
}) {
  const { http } = global.services;
  const { t } = useTranslation();
  const [photo, setPhoto] = useState([]);
  const [preview, setPreview] = useState([]);
  const [adultContent, setAdultContent] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});
  const uploadSizeModal = useDialogHook(UploadSizeModal);
  const [progress, setProgress] = useState([]);
  const [uploading, setUploading] = useState(0);
  const [steps, setSteps] = useState(UPLOAD); //Upload or Rename
  React.useEffect(() => {
    if (photo) {
      setPhotoPreview(photo, progress);
    }
  }, [photo, progress]);

  const setPhotoPreview = (_photo = photo, _progress = progress) => {
    /**
     * @type {Array<{ id: string, name: string, size: number, path: string}>}
     */
    const _previews = [];
    _photo.forEach((file, index) => {
      let size = _photo[index].size;

      if (size > MB) {
        size /= MB;
        size = `${Math.round(size * 10) / 10}MB`;
      } else if (size > KB) {
        size /= KB;
        size = `${Math.round(size * 10) / 10}KB`;
      } else {
        size = `${size}B`;
      }

      _previews.push({
        id: index,
        name: _photo[index].name,
        size: size,
        path: URL.createObjectURL(photo[index]),
        order: index,
        progressId: _photo[index].progressId,
        progress: _progress[_photo[index].progressId]
          ? _progress[_photo[index].progressId]
          : 1,
        media_id: _photo[index]?.media_id || null,
        title: _photo[index].name.split(".")[0],
        action: null,
        rename: false,
        file_extension: _photo[index].name.split(".")[1],
      });
    });
    setUploading((prevUploading) => uploadingFiles(_previews));
    setPreview(_previews);
  };

  const uploadingFiles = (_previews) => {
    let uploadingFiles = _previews.filter((upload) => upload.progress < 100);
    return uploadingFiles.length;
  };
  /**
   *
   * @param {string} index
   */
  const removeUploadedPhoto = (index, progress_id) => {
    const updatedPhotos = photo.filter((_, i) => _.progressId !== progress_id);
    setPhoto(updatedPhotos);
  };

  const submit = async () => {
    setSubmitLoading(true);

    let errors = {};
    setErrorMessages(errors);

    try {
      let payload = new FormData();
      for (let i = 0; i < preview.length; i++) {
        payload.append(`media[${i}]`, JSON.stringify(preview[i]));
      }
      payload.append("is_adult_content", adultContent);
      const upload = await onUploadImagesVideos(payload);

      if (upload === "success") {
        hide(true);
      }
    } catch (err) {
      if (err.status === 422) {
        errors = {
          ...errors,
          ...err.data?.errors,
        };

        console.log("::AddPhotoModal > 422 errors", errors);

        return setErrorMessages((prev) => ({
          ...prev,
          ...errors,
        }));
      }

      console.log("::AddPhotoModal > error", err);
      setSubmitLoading(false);
    } finally {
      setSubmitLoading(false);
    }
  };

  const toggleAdultContentCheckbox = (e) => {
    setAdultContent(!e.target.checked);
  };

  const showUploadSizeModalError = () => {
    uploadSizeModal({}, (callback) => {
      console.log("callback", callback);
    });
  };

  const renamePhoto = async (param) => {
    try {
      let payload = {
        media_id: param.media_id,
        title: param.title,
      };
      const { data } = await http.post(
        `public/photo-rename/${param.media_id}`,
        payload
      );
      if (data) {
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="smooth-dnd-parent">
      <Modal
        width="w-full md:w-15/20 2md:w-11/20 lg:w-9/20 xl:w-6/20"
        contentClassName="rounded-lg overflow-auto custom-scroll"
        wrapperClassName="overflow-visible"
      >
        <div className="w-full">
          {/* header */}
          {setSteps === UPLOAD ? (
            <ModalHeader
              title={`${
                uploading > 0
                  ? `${t("uploading")} (${uploading})`
                  : t("uploadFile")
              } `}
              hide={hide}
            />
          ) : (
            <ModalHeader
              title={`${
                preview.length > 0 && steps === RENAME
                  ? `${t("uploading")} (${preview.length})`
                  : t("uploadFile")
              }`}
              hide={hide}
            />
          )}
          {/* content */}
          <div className="px-8.5 pt-3 pb-4">
            {steps === RENAME && (
              <div className="pb-2">
                <p>{t("re-orderPhotos")}</p>
                <p>{t("editPhotoNames")}</p>
              </div>
            )}
            <div className="flex flex-column">
              {/* dropzone */}
              {steps === UPLOAD && (
                <DropzoneUploadGallery
                  userData={userData}
                  linkType={preview}
                  setProgress={setProgress}
                  setItems={setPhoto}
                  onUploadMultiple={(files) => {
                    for (const file of files) {
                      if (file.size > maxUploadSize) {
                        showUploadSizeModalError();
                        return;
                      }
                    }
                    setPhoto((prev) => [...files, ...prev]);
                    // setPhotoPreview(files)
                  }}
                />
              )}

              {Array.isArray(preview) && (
                <div className="mt-2">
                  <ImageDisplay
                    previews={preview}
                    onRemove={() => {}}
                    setPreview={setPreview}
                    photo={photo}
                    progress={progress}
                    removeUploadedPhoto={removeUploadedPhoto}
                    action={steps === RENAME ? "edit" : "add"}
                    renamePhoto={renamePhoto}
                    withExtension={false}
                  />
                </div>
              )}

              <div className="flex space-x-4.5 items-center mt-8 checkbox-v2">
                {photo.length > 0 && (
                  <div>
                    <CustomCheckbox
                      label={
                        <p className="checkbox-text">
                          {
                          photo.length > 1 
                          ? t('thisFileDoesNotContainAdult2', {file: t('photos')?.toLowerCase?.() })
                          : t('thisFileDoesNotContainAdult', {file: t('photo') })
                        }
                        </p>
                      }
                      name={"termsAndConditions"}
                      checked={!adultContent}
                      onChange={(e) => toggleAdultContentCheckbox(e)}
                      v2={true}
                    />
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="absolute left-0 w-full custom-modal-footer-separator border-t border-gray my-4"></div>
              <div className="w-full flex items-center justify-start 1sm:justify-end space-x-2 mt-10">
                <Button
                  buttonName={t("cancel")}
                  buttonClass="bg-thBg py-4 px-8 hover:bg-gray"
                  buttonTextClass="text-black text-sm font-semibold"
                  onClick={() => hide()}
                  disabled={
                    submitLoading || preview.length < 0 || uploading > 0
                  }
                />
                <Button
                  key="save-btn-category"
                  buttonName={`${steps === UPLOAD ? t("next") : t("save")}`}
                  buttonClass="bg-primary py-4 px-8 hover:bg-darkPrimary"
                  buttonTextClass="text-white text-sm font-semibold flex items-center"
                  onClick={() => {
                    if (steps !== UPLOAD) {
                      submit();
                    } else {
                      setSteps(RENAME);
                    }
                  }}
                  disabled={
                    submitLoading || preview.length <= 0 || uploading > 0
                  }
                  loadingIcon={
                    submitLoading ? (
                      <LoadingIndicator className="text-white w-4 h-4 ml-2" />
                    ) : null
                  }
                />
              </div>
            </div>
          </div>
          {/* end content */}
        </div>
      </Modal>
    </div>
  );
}

export const ImageDisplay = ({
  previews,
  setPreview,
  removeUploadedPhoto,
  action = "add",
  setDataSumbit = () => {},
  dataSubmit = [],
  renamePhoto = () => {},
  withExtension = true
}) => {
  const { t } = useTranslation();
  const onDrop = (dropResult) => {
    const { removedIndex, addedIndex } = dropResult;

    const reorderedItems = reorder(previews, removedIndex, addedIndex);
    setPreview(reorderedItems);
  };
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    // Update the order attribute
    result.forEach((item, index) => {
      item.order = index;
    });
    return result;
  };

  const onSaveName = async (index, id) => {
    setPreview((prev) => {
      const preview = _.cloneDeep(prev);
      preview[index].rename = false;
      preview[index].name =
        preview[index].title + "." + preview[index].file_extension;

      return preview;
    });
    renamePhoto({
      title: previews[index].title,
      media_id: previews[index].media_id,
    });
    setDataSumbit(
      dataSubmit.map((item) =>
        item.id === id ? { ...item, action: "rename" } : item
      )
    );
  };

  const toggleRename = (index) => {
    setPreview((prev) => {
      const preview = _.cloneDeep(prev);
      preview[index].rename = true;
      return preview;
    });
  };

  function handleKeyPress(e, index, id) {
    if (e.key === "Enter") {
      onSaveName(index, id);
    }
  }

  const onRename = (e, index, id) => {
    setPreview((prev) => {
      const title = _.cloneDeep(prev);
      title[index].title = e.target.value;
      return title;
    });
    setDataSumbit(
      dataSubmit.map((item) =>
        item.id === id ? { ...item, title: e.target.value } : item
      )
    );
  };

  return (
    <div>
      <Container
        groupName="1"
        dropPlaceholder={false}
        getGhostParent={() => document.querySelector(".smooth-dnd-parent")}
        shouldAnimateDrop={() => false}
        dragClass="tilt"
        // getChildPayload={(i) => previews[i]}
        dragHandleSelector=".dragHandleSelector-preview"
        onDrop={onDrop}
      >
        {previews.map((prev, index) => (
          <Draggable key={index}>
            <div className="flex gap-1 items-center">
              <span className="text-white rounded-full min-w-5 font-semibold leading-5 bg-black text-xs text-center">
                {index + 1}
              </span>
              <span className="draggable-item dragHandleSelector-preview">
                <RxDragHandleDots2 className="text-2xl color-gray dnd-handle cursor-pointer" />
              </span>
              <div className="flex overflow-hidden pr-2 flex-row items-center mb-2 rounded-lg border-solid border-gray border w-full bg-white">
                <img
                  src={prev.path}
                  className="w-14 h-14 mr-4 object-cover"
                  alt="preview"
                />
                <div className="flex-1 flex-row items-center">
                  <div className="flex justify-between w-full">
                    {prev.rename ? (
                      <input
                        type="text"
                        className="border-none p-0-important w-full"
                        value={prev?.title}
                        onChange={(e) => onRename(e, index, prev.id)}
                        onKeyDown={(e) => handleKeyPress(e, index, prev.id)}
                      />
                    ) : (
                      <p className="break-all">
                        {/* {`${prev?.name.slice(0,18)}...`} */}
                        {withExtension ? prev?.name : prev?.name?.replace?.(/\.[0-9a-zA-Z]+$/gi, "")}
                      </p>
                    )}
                    {action !== "edit" && (
                      <div
                        className="w-11 flex justify-end"
                        onClick={() =>
                          removeUploadedPhoto(index, prev?.progressId)
                        }
                      >
                        <TrashIconSVG className="cursor-pointer" />
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2 items-center">
                    {!prev.rename && (
                      <span className="w-14 text-sm text-darkerGray font-semiBold">
                        {prev.size}
                      </span>
                    )}
                    {prev.progress && action !== "edit" && (
                      <div className="w-full flex items-center">
                        <div className="bg-thBg h-2 rounded-full w-full mr-2">
                          <div
                            className={`h-2 rounded-full ${
                              prev.progress === 100
                                ? "bg-lightGreen"
                                : "bg-primary"
                            }`}
                            style={{ width: `${prev.progress}%` }}
                            id="serverProgress"
                          ></div>
                        </div>
                        <span className="w-11 flex justify-end">
                          {prev.progress}%
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                {action === "edit" && (
                  <div className="flex text-sm">
                    {prev.rename ? (
                      <div
                        onClick={() => onSaveName(index, prev.id)}
                        className="cursor-pointer text-lightGreen"
                      >
                        {prev.loading ? (
                          <LoadingIndicator className="text-lightGreen w-3 h-3 mr-1 inline" />
                        ) : (
                          <CheckIconSVG className="inline mr-1" />
                        )}
                        {t("saveName")}
                      </div>
                    ) : (
                      <>
                        <div
                          className="cursor-pointer"
                          onClick={() => toggleRename(index)}
                        >
                          <EditPencilIconSVG className="inline mr-1" />
                          {t("rename")}
                        </div>
                        <div
                          className="w-9 flex justify-end"
                          onClick={() => removeUploadedPhoto(index, prev.id)}
                        >
                          <TrashIconSVG className="cursor-pointer" />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Draggable>
        ))}
      </Container>
    </div>
  );
};
